import { Grid, useEventCallback } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';
import MunicipalityList, {
  ID_ALTRA_PROVINCIA,
  ID_ALTRO_COMUNE,
  LABEL_ALTRA_PROVINCIA,
  MunicipalityAnpr,
  MunicipalityAnprVM,
} from 'shell/components/shared/municipality-list';
import { ItalianForm } from 'shell/view-models/italian-form';
import { memo, useEffect } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { DateTime } from 'luxon';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';

interface ItalianComponentProps {
  disabledComponent?: boolean;
  values?: ItalianForm;
  errors?: FormikErrors<ItalianForm>;
  touched?: FormikTouched<ItalianForm>;
  onChange: (key: keyof ItalianForm, value: ItalianForm[keyof ItalianForm]) => void;
  onChanges: (values: ItalianForm) => void;
  labelMunicipality?: string;
  labelProvince?: string;
  fromDate?: DateTime | null;
  idDominio?: number;
  idValore?: string;
  required?: boolean;
  isResidence?: boolean;
}

const ItalianComponent = ({
  disabledComponent,
  onChange,
  onChanges,
  values,
  errors,
  touched,
  labelMunicipality = 'Comune di nascita',
  labelProvince = 'Provincia di nascita',
  fromDate,
  idDominio,
  idValore,
  required,
  isResidence = false,
}: ItalianComponentProps): React.ReactElement => {
  const getData = useHttpClient(
    `${__ACT_API_URL__}/valori-dominio-atti/comune-anpr/dominio/${idDominio}/valore/${idValore}`,
    {
      options: {
        method: 'GET',
        loading: true,
        errorCatching: true,
      },
    }
  );

  useEffect(() => {
    if (idDominio && idValore) {
      (getData() as ResponsePromise<HttpResponse<MunicipalityAnpr>>).then((response) => {
        const castedResponse: MunicipalityAnpr | undefined = response?.data.payload || undefined;

        const municipality: MunicipalityAnprVM = {
          id: castedResponse?.codiceIstat,
          description: castedResponse?.denominazione || '',
          provinceCode: castedResponse?.idProvinciaIstat?.toString() || '',
          province: castedResponse?.codiceSiglaProvincia || '',
          startDate: castedResponse?.dataIstituzione,
          endDate: castedResponse?.dataCessazione,
          code: castedResponse?.key?.codice || undefined,
          idProvince: castedResponse?.idProvincia
            ? Number.parseInt(castedResponse?.idProvincia, 10)
            : undefined,
        };

        onChanges({
          municipality,
          otherMunicipality: '',
        });
      });
    }
  }, [idDominio, idValore]);

  const handleMunicipalityListChange = useEventCallback(
    (municipality: MunicipalityAnprVM | null) => {
      onChanges({
        municipality: municipality
          ? ({
              ...(municipality || {}),
              province:
                municipality?.code?.toString() === ID_ALTRO_COMUNE
                  ? LABEL_ALTRA_PROVINCIA
                  : municipality?.province || '',
              idProvince:
                municipality?.code?.toString() === ID_ALTRO_COMUNE
                  ? ID_ALTRA_PROVINCIA
                  : municipality?.idProvince || '',
              provinceCode:
                municipality?.code?.toString() === ID_ALTRO_COMUNE
                  ? ID_ALTRA_PROVINCIA
                  : municipality?.provinceCode || '',
            } as MunicipalityAnprVM)
          : null,
        otherMunicipality: '',
      });
    }
  );

  return (
    <Grid container spacing={2} item style={{ paddingTop: '0px' }}>
      <Grid item xs={12} md={3}>
        <MunicipalityList
          value={values?.municipality || null}
          onChange={handleMunicipalityListChange}
          required={required}
          disabled={disabledComponent}
          label={labelMunicipality}
          fromDate={fromDate}
          error={
            (touched?.municipality as unknown as FormikTouched<MunicipalityAnprVM>) &&
            Boolean(errors?.municipality as FormikErrors<MunicipalityAnprVM>)
          }
          helperText={errors?.municipality}
        />
      </Grid>
      {values?.municipality?.code?.toString() === ID_ALTRO_COMUNE && !isResidence ? (
        <Grid item xs={12} md={4}>
          <PmiTextField
            aria-label="Altro comune"
            label="Altro comune *"
            id="otherMunicipality"
            name="otherMunicipality"
            fullWidth
            disabled={disabledComponent}
            type="text"
            variant="outlined"
            value={values?.otherMunicipality || ''}
            onChange={(event) => {
              onChange('otherMunicipality', event.target.value);
            }}
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label={labelProvince}
          label={`${labelProvince} ${required ? '*' : ''}`}
          id={`province${labelMunicipality}`}
          name="province"
          fullWidth
          disabled={values?.municipality?.code?.toString() !== ID_ALTRO_COMUNE}
          type="text"
          variant="outlined"
          value={values?.municipality?.province || ''}
          onChange={(event) => {
            onChange('municipality', {
              ...values?.municipality,
              province: event.target.value,
            } as MunicipalityAnprVM);
          }}
        />
      </Grid>
    </Grid>
  );
};

ItalianComponent.displayName = 'Italian generality component';

const Italian = memo(ItalianComponent);

export default Italian;
